<template>
    <div>
        <div class="content-header">
            <div class="container-fluid">
                <div class="row mb-2">
                    <div class="col-sm-6">
                        <h1 class="m-0  text-capitalize">Game Analytic</h1>
                    </div>
                    <div class="col-sm-6">
                        <ol class="breadcrumb float-sm-right">
                            <li class="breadcrumb-item">
                                <router-link to="/">Home</router-link>
                            </li>
                            <li class="breadcrumb-item active text-capitalize"><i class="fas fa-list"></i> Game Analytic
                            </li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>

        <section class="content">
            <div class="container-fluid">
                <data-table-wrapper
                    ref="dataTable"
                    :columns="columns"
                    :filters="filters"
                    :filter-defaults="filterDefaults"
                    :cell-style-option="cellStyleOption"
                    :on-data-load="onDataLoad"
                    disable-primary-column="true"
                    disable-edit-action="true"
                    disable-delete-action="true"
                    :endpoint="'/game-analytic'"
                    api-service="analytics"
                    sort-column-default="timestamp"
                    sort-order-default="DESC"
                >
                    <template v-slot:filter-buttons>
                        <button class="btn btn-default float-right" @click="doExport">
                            <span v-if="!exportInProgress">
                                <i class="fa fa-cloud-download"></i> {{ $t('Export') }}
                            </span>
                            <span v-else>
                                <i class="fas fa-spinner fa-pulse"></i> {{ $t('Export') }}
                            </span>
                        </button>
                        <button class="btn btn-default float-right mr-2" @click="doAiAnalysis">
                            <span v-if="!aiInProgress">
                                <i class="fas fa-robot"></i> {{ $t('AI Analysis') }}
                            </span>
                            <span v-else>
                                <i class="fas fa-spinner fa-pulse"></i> {{ $t('AI Analysis') }}
                            </span>
                        </button>
                    </template>
                </data-table-wrapper>
            </div>

        </section>
    </div>
</template>

<script>
import DataTableWrapper from '../components/DataTableWrapper';
import {mapGetters} from "vuex";
import VueJsonPretty from 'vue-json-pretty';
import 'vue-json-pretty/lib/styles.css';
import * as dateUtils from "@/helpers/dateUtils";
import * as formatUtils from "@/helpers/formatUtils";
import {openAiAnalysis} from "@/services/aiAnalysis";

export default {
    components: {
        DataTableWrapper,
        VueJsonPretty
    },
    data() {
        return {
            exportInProgress: false,
            aiInProgress: false,
            onDataLoad: () => {
                let els = document.getElementsByClassName('vjs-tree');
                Array.from(els).forEach((el) =>  {
                    if (el?.firstElementChild?.firstElementChild?.firstElementChild?.firstElementChild?.firstElementChild?.innerText === '{') {
                        el.firstElementChild.firstElementChild.firstElementChild.firstElementChild.firstElementChild.click();
                    }
                })
            },
            cellStyleOption: {
                bodyCellClass: ({ column }) => {
                    if (column.field === 'state' || column.field === 'sessionKey') {
                        return 'small-text';
                    }
                },
            },
            filters: [
              {
                field: 'fromDate',
                placeholder: 'From Date',
                label: 'From Date',
                type: 'date-picker',
                shortcuts: dateUtils.getFilterShortcuts()
              },
              {
                field: 'toDate',
                placeholder: 'To Date',
                label: 'To Date',
                type: 'date-picker',
                shortcuts: dateUtils.getFilterShortcuts()
              },
            ],
        };
    },
    computed: {
        ...mapGetters([
            'getGames',
            'getGame',
            'getPartners',
            'getPartner',
            'getOperators',
            'getOperator',
            'getCurrency',
            'isOperator',
        ]),
        columns() {
            let columns = [
                {
                    field: 'gameid',
                    title: 'Game',
                    sorting: true
                },
                {
                    field: 'name',
                    title: 'Name',
                    sorting: true
                },
                {
                    field: 'c',
                    title: 'Amount of users',
                    sorting: false,
                },
                {
                    field: 'r1',
                    title: 'Amount of users returning 1 day',
                    sorting: false,
                },
                {
                    field: 'r7',
                    title: 'Amount of users returning 7 day',
                    sorting: false,
                },
                {
                    field: 'r30',
                    title: 'Amount of users returning 30 day',
                    sorting: false,
                },
                {
                    field: 'r1_perc',
                    title: '% of users returning 1 day',
                    sorting: false,
                    callback: ({row}) => this.formatNumber(row.r1_perc),
                },
                {
                    field: 'r7_perc',
                    title: '% of users returning 7 day',
                    sorting: false,
                    callback: ({row}) => this.formatNumber(row.r7_perc),
                },
                {
                    field: 'r30_perc',
                    title: '% of users returning 30 day',
                    sorting: false,
                    callback: ({row}) => this.formatNumber(row.r30_perc),
                },
                {
                    field: 'more_than_1_session',
                    title: 'More than 1 session',
                    sorting: false,
                },
                {
                    field: 'players_with_1_session_only',
                    title: 'Players with 1 session only',
                    sorting: false,
                },
                {
                    field: 'all_users',
                    title: 'All users',
                    sorting: false,
                },
                {
                    field: 'session_length',
                    title: 'Session length (mins)',
                    sorting: false,
                },
                {
                    field: 'sesions_per_user',
                    title: 'Sesions per user',
                    sorting: false,
                    callback: ({row}) => this.formatNumber(row.sesions_per_user),
                },
                {
                    field: 'churn_rate',
                    title: 'Churn rate',
                    sorting: false,
                },
                {
                    field: 'mau',
                    title: 'MAU',
                    sorting: false,
                    callback: ({row}) => this.formatNumber(row.mau),
                },
                {
                    field: 'dau',
                    title: 'DAU',
                    sorting: false,
                    callback: ({row}) => this.formatNumber(row.dau),
                },
                {
                    field: 'stickness',
                    title: 'stickiness',
                    sorting: false,
                    callback: ({row}) => this.formatNumber(row.stickness),
                },
                {
                    field: 'loaded_percent',
                    title: 'loaded percent',
                    sorting: false,
                    callback: ({row}) => this.formatNumber(row.loaded_percent),
                },
                {
                    field: 'has_spins_percent',
                    title: 'Has spins percent',
                    sorting: false,
                    callback: ({row}) => this.formatNumber(row.has_spins_percent),
                },
            ];
            return columns;
        },
        filterDefaults() {
            return {
              fromDate: dateUtils.getTodayDate()
            }
        }
    },
    methods: {
        formatNumber(value) {
          return formatUtils.formatNumberToFixed(value,2);
        },
        doExport() {
            if (this.exportInProgress) {
                return;
            }
            this.exportInProgress = true;
            this.$exportService.doExport('game-analytic', this.$refs.dataTable.getPageQuery()).finally(() => {
                this.exportInProgress = false;
            });
        },
        doAiAnalysis() {
            if (this.aiInProgress) {
                return;
            }
            this.aiInProgress = true;
            openAiAnalysis(this.$http, 'game-analytic/ai-analysis', this.$refs.dataTable.getPageQuery())
                .finally(() => {
                    this.aiInProgress = false;
                });
        },
    },
    mounted() {
    },
};
</script>
